import { inject, NgModule } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { AppConfig } from '@app/app.config';
import { FactorRowService } from '@app/modules/factor-row/services/factor-row.service';
import { IButtonExtended } from '@components/button/interfaces/button.interface';
import {
    FileComponent_InitItemButtons,
    FileComponent_OnButtonAction,
    IFileComponent_InitItemButtons,
    IFileComponent_OnButtonAction,
} from '@components/fields/file/interfaces/file.interface';
import { LANG_DICTIONARY_TOKEN } from '@constants/diConstant';
import { SocketsService } from '@fui/fui-sockets';
import { MoaComponent } from '@submodules/MOA/components/moa.component';
import { SERVER_YII_URL_TOKEN } from '@submodules/MOA/diConstant';
import { getServerYiiUrl } from '@submodules/MOA/factoryConstants';
import { MOAFactorDataInterface } from '@submodules/MOA/interfaces/moa.interface';
import { MoaConfig } from '@submodules/MOA/moa.config';
import { MoaService } from '@submodules/MOA/services/moa.service';
import {
    FactorReportDialogComponent_AddItemButtons,
    FactorReportDialogComponent_ButtonClick,
    IFactorReportDialogComponent_AddItemButtons,
    IFactorReportDialogComponent_ButtonClick,
} from 'src/shared/sockets/factor/dialogs/factor-report-dialog.interface';

@NgModule({
    imports: [MoaComponent],
    exports: [RouterModule],
    providers: [
        MoaService,
        MoaConfig,
        { provide: SERVER_YII_URL_TOKEN, useFactory: getServerYiiUrl, deps: [MoaConfig] },
        { provide: AppConfig, useExisting: MoaConfig },
    ],
})
export class MoaModule {
    private readonly socketService = inject(SocketsService);
    private factorData: MOAFactorDataInterface;

    constructor() {
        this.socketService.addInitializer(() => this.init());
    }

    /**
     * Инициализация модуля "МОП"
     */
    init() {
        const router = inject(Router);
        const dictionary = inject(LANG_DICTIONARY_TOKEN);
        const serviceRowFactor = inject(FactorRowService);

        const routerConfig = router.config;
        routerConfig.unshift({
            path: 'moa/editor/:id/file/:ext/ext',
            component: MoaComponent,
        });
        routerConfig.unshift({
            path: 'moa/load/:factor_id/factor/:report_reglam_id/report_reglam',
            component: MoaComponent,
        });
        router.resetConfig(routerConfig);

        // Подписка: получить данные реестра и его карточки
        serviceRowFactor.factorData$.subscribe((data) => {
            this.factorData = data;
        });

        // Подписка: открыть редактор документов, МОП
        this.socketService.subscribe<IFileComponent_OnButtonAction>(
            'Moa',
            FileComponent_OnButtonAction,
            async (component: IFileComponent_OnButtonAction) => {
                const type = component.type;
                const value = component.value;
                const moduleName = component.moduleName;

                const factor_id = this.factorData?.factorId;
                const factor_name = this.factorData?.factorName;
                const factor_data_id = this.factorData?.factorDataId;
                const factor_data_name = this.factorData?.factorDataName;

                switch (type) {
                    case 'openEditorMOA': {
                        const baseParams: any = {
                            module: moduleName,
                        };

                        if (moduleName === 'factor') {
                            baseParams.factor_id = factor_id;
                            baseParams.factor_name = factor_name;
                            baseParams.factor_data_id = factor_data_id;
                            baseParams.factor_data_name = factor_data_name;
                        }

                        const url = router.createUrlTree(['/moa/editor', value.id, 'file', value.ext, 'ext'], {
                            queryParams: baseParams,
                        });

                        window.open(router.serializeUrl(url), '_blank');
                        break;
                    }
                }
            },
        );

        // Подписка: Показать пиктограммы в компонентах
        this.socketService.subscribe<IFileComponent_InitItemButtons>(
            'Moa',
            FileComponent_InitItemButtons,
            async (response: IFileComponent_InitItemButtons) => {
                const data = response.data;
                // Карта пиктограммы
                const fileIconMap = {
                    doc: 'file-word-box',
                    docx: 'file-word-box',
                    xls: 'file-excel-box',
                    xlsx: 'file-excel-box',
                    ppt: 'file-powerpoint-box',
                    pptx: 'file-powerpoint-box',
                    pdf: 'file-pdf-box',
                } as const;

                // Карта поддержки расширении файлов в модулях
                const supportedExtensionMap = {
                    factor: ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'pdf'],
                    report_reglam: ['doc', 'docx', 'xls', 'xlsx'],
                } as const;

                const supportedExtensions = supportedExtensionMap[data.module];
                if (!supportedExtensions) {
                    return;
                }

                const hasValidFile = data.fileList.some((list) => supportedExtensions.includes(list.ext));
                if (!hasValidFile) {
                    return;
                }

                const validFile = data.fileList.find((list) => supportedExtensions.includes(list.ext));
                const iconName = validFile ? fileIconMap[validFile.ext] : fileIconMap['doc'];

                const itemButtons = response.itemButtons;
                const itemButtonDictionaryName: string | null = response.translateName;

                itemButtons.push({
                    index: 1,
                    name: itemButtonDictionaryName,
                    icon: iconName,
                    click: 'openEditorMOA',
                });
            },
        );

        // Добавить кнопки "Открыть в редакторе" в список "Печатные формы"
        this.socketService.subscribe<IFactorReportDialogComponent_AddItemButtons>(
            'Moa',
            FactorReportDialogComponent_AddItemButtons,
            async (data) => {
                const button: IButtonExtended = {
                    title: dictionary.openEditorMOA,
                    icon: 'text-box-edit-outline',
                    type: 'button-icon',
                    borderRadius: 'circle',
                    height: 'slim',
                    color: 'neutral',
                    iconSizeRem: 1,
                    click: 'open_moa_editor',
                };
                for (const item of data.items) {
                    item.buttons.unshift(button);
                }
            },
        );

        // Обработать нажатие "Открыть в редакторе" в списке "Печатные формы"
        this.socketService.subscribe<IFactorReportDialogComponent_ButtonClick>(
            'Moa',
            FactorReportDialogComponent_ButtonClick,
            async (data) => {
                if (data.command === 'open_moa_editor') {
                    const queryParams = {
                        module: 'exportReportReglam',
                        name: data.item.name,
                        factor_name: data.item.factor_name,
                        id_props_type: data.item.id_props_type,
                        id_props_type_rubricator: data.item.id_props_type_rubricator,
                        is_link_prop: data.item.is_link_prop,
                        is_map: data.item.is_map,
                    };
                    const url = router.createUrlTree(
                        ['/moa/load', data.item.factor_id, 'factor', data.item.id, 'report_reglam'],
                        {
                            queryParams,
                        },
                    );

                    window.open(router.serializeUrl(url), '_blank');
                }
            },
        );
    }
}
